import React, { useEffect, useState } from 'react';
import { ActivityService } from '@vanilla/services/ActivityService.js';
import { ToastManager } from '@/lib/toast.js';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import AutoGrowTextarea from '@/components/ui/AutoGrowTextarea.jsx';
import ActivityFormModal from './ActivityFormModal.jsx';

/**
 * Add/Edit Task dialog. When dealId is missing, showDealPicker + dealOptions enable calendar quick-create.
 */
export default function TaskFormDialog({
    open,
    mode = 'add',
    dealId: dealIdProp,
    initial = null,
    users = [],
    showDealPicker = false,
    dealOptions = [],
    onSaved,
    onClose,
}) {
    const [subject, setSubject] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [assignee, setAssignee] = useState('');
    const [dealId, setDealId] = useState(dealIdProp || '');

    useEffect(() => {
        if (!open) return;
        setSubject(initial?.subject || '');
        setDueDate(initial?.dueDate ? String(initial.dueDate).slice(0, 10) : '');
        setAssignee(initial?.assignedTo || '');
        setDealId(dealIdProp || initial?.dealId || '');
    }, [open, initial, dealIdProp]);

    if (!open) return null;

    return (
        <ActivityFormModal
            title={mode === 'add' ? 'Add Task' : 'Edit Task'}
            onClose={onClose}
            onSave={async () => {
                const trimmed = subject.trim();
                if (!trimmed) return false;
                const effectiveDealId = dealId || dealIdProp;
                if (!effectiveDealId) {
                    ToastManager.show({ title: 'Task', message: 'Select a deal first.', tone: 'warning' });
                    return false;
                }
                try {
                    if (mode === 'add') {
                        await ActivityService.addTask('deal', effectiveDealId, trimmed, dueDate || '', '', assignee || '');
                    } else if (initial?.id) {
                        await ActivityService.updateActivity(initial.id, {
                            subject: trimmed,
                            dueDate: dueDate || null,
                            mentions: assignee ? [assignee] : [],
                        });
                    }
                    await onSaved?.({ dealId: effectiveDealId });
                    return true;
                } catch (err) {
                    ToastManager.show({ title: 'Task', message: err?.message || 'Save failed.', tone: 'error' });
                    return false;
                }
            }}
        >
            {showDealPicker && !dealIdProp ? (
                <div className="field-group">
                    <label>Deal <span className="required-asterisk">*</span></label>
                    <NativeSelect unstyled className="form-input" value={dealId} onChange={(e) => setDealId(e.target.value)}>
                        <option value="">Select deal…</option>
                        {dealOptions.map((d) => (
                            <option key={d.id} value={d.id}>{d.name || d.dealName || d.id}</option>
                        ))}
                    </NativeSelect>
                </div>
            ) : null}
            <div className="field-group">
                <label>Subject <span className="required-asterisk">*</span></label>
                <AutoGrowTextarea
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Enter task subject"
                    minRows={2}
                    maxHeightPx={160}
                    aria-label="Task subject"
                />
            </div>
            <div className="field-group">
                <label>Due Date</label>
                <Input unstyled type="date" className="form-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div className="field-group">
                <label>Assignee</label>
                <NativeSelect unstyled className="form-input" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                        <option value={u.id} key={u.id}>{u.name || u.email || u.id}</option>
                    ))}
                </NativeSelect>
            </div>
        </ActivityFormModal>
    );
}
